/**
 * DomainDetailPane — right column of the smart inbox view.
 *
 * Shows domain header + AI summary banner + topic groups with per-item
 * actions when a domain bundle is selected.
 */
import React, { useState } from 'react';
import { EmptyState } from '../../../ui';
import { DomainBadge } from '../shared/DomainBadge';
import { UrgencyIndicator } from '../shared/UrgencyIndicator';
import { DomainSummaryBanner } from './DomainSummaryBanner';
import { TopicGroup } from './TopicGroup';

function DomainDetailPane({ bundle }) {
  const [openGroupIdx, setOpenGroupIdx] = useState(-1);

  // Reset open group when domain changes
  const domainKey = bundle?.domainKey;
  const [prevDomain, setPrevDomain] = useState(domainKey);
  if (domainKey !== prevDomain) {
    setPrevDomain(domainKey);
    setOpenGroupIdx(-1);
  }

  if (!bundle) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState title="Select a domain" body="Choose a domain from the list to see its summary." size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      {/* Domain header — pinned */}
      <div style={{ flexShrink: 0, padding: '10px 16px', borderBottom: '1px solid var(--surface)' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <DomainBadge domain={bundle.domainKey} />
          <span className="text-heading-sm text-text-dark">
            {bundle.domainLabel}
          </span>
          <UrgencyIndicator urgency={bundle.highestUrgency} />
          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            {bundle.unreadCount > 0 && (
              <span className="text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full bg-red-critical text-white">
                {bundle.unreadCount} unread
              </span>
            )}
            <span className="text-body-sm text-text-muted">
              {bundle.totalCount} email{bundle.totalCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        <div className="flex flex-col gap-3">
          {/* AI Summary */}
          <DomainSummaryBanner summary={bundle.summary} />

          {/* Topic groups — single-open accordion */}
          <div className="flex flex-col gap-1.5">
            {bundle.topicGroups.map((group, idx) => (
              <TopicGroup
                key={`${group.label}-${idx}`}
                group={group}
                isOpen={openGroupIdx === idx}
                onToggle={() => setOpenGroupIdx(openGroupIdx === idx ? -1 : idx)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { DomainDetailPane };
